import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { Modal } from 'flowbite';
import { APP } from 'src/app/config/app';
import { IAvailabilities, IRouteDetails, IServiceInfoDetail, IVehicle } from 'src/app/models';
import { ExpeditionStatus, NotificationTypes } from 'src/app/models/enums';
import { IBulkActionType } from 'src/app/models/enums/bulkActionType';
import { AvailabilitiesResolver } from 'src/app/resolvers';
import { LiteralService, RouteService, StorageService, UsersService, UtilsService, VehiclesService, NotificationService } from 'src/app/services';
import { ServiceInfoService } from 'src/app/services/service-info/service-info.service';
import { TooltipPosition } from 'src/app/shared/tooltip/tooltip-position';
import { initialAvailabilitiesState } from 'src/app/store/initialisation/availabilities';
import { images } from 'src/images';
import { AppComponent } from 'src/app/app.component';
import { ModalRouteDeparturesComponent } from 'src/app/shared/modals/modal-route-departures/modal-route-departures.component';
import {
  AVAILABILITY_FILTER_KEYS,
  IAvailabilitiesFilters,
  buildQueryString,
  deserializeAvailabilitiesFilters,
  serializeAvailabilitiesFilters,
} from 'src/app/filters';

@Component({
  selector: 'app-availabilities',
  templateUrl: './availabilities.component.html',
  styleUrl: './availabilities.component.scss',
  standalone: false
})
export class AvailabilitiesComponent implements OnInit, OnDestroy {

  // Embedded mode: rendered inside the setup page, scoped to one setup, without
  // breadcrumb ownership; the data is self-fetched instead of resolver-driven
  @Input() embedded = false;
  @Input() setupId?: number;

  public availabilities = initialAvailabilitiesState;
  public routeId: number;
  public route?: IRouteDetails;
  public serviceInfo: IServiceInfoDetail | undefined;
  public serviceInfoId: number | undefined;
  public serviceName: string = '';
  public routeName: string = '';
  public bookings: any[]

  public interval: any;
  public searching = false;

  public images = images;

  public bulkAction = '';
  public allAvailbilitiesSelected = false;

  public changeDriverVehicleModal: Modal;
  public changeDriverVehicleShowed = false;
  public createBackupModal: Modal;
  public createBackupShowed = false;
  public managerClientId?: number;
  public vehicles: any[] = [];
  public driverSelected: any;
  public vehicleSelected: any;
  public dateTimeSelected: any;
  public timeSelected: any;
  public commentSelected: any;
  public singleAvailbilitySelected: IAvailabilities;
  public deleteModal: Modal;
  public deleteShowed = false;
  public deleteModalTitle = 'availabilities.actions.deleteAvailabilities.title';
  public deleteModalText = 'availabilities.actions.deleteAvailabilities.text';
  public hasBookings = false;
  public hasTrips = false;
  public hasTicketing = false;
  public isRoundTrip = false;
  public loadingAction = false;
  public reason: string;

  public ExpeditionStatus = ExpeditionStatus;
  public TooltipPosition = TooltipPosition;

  public searchValue = '';

  public departuresModal: Modal;
  public departuresShowed = false;
  public creatingDepartures = false;
  public departuresSetups: { value: string; label: string }[] = [];
  @ViewChild(ModalRouteDeparturesComponent) departuresModalComponent: ModalRouteDeparturesComponent;

  private queryParamsSub?: Subscription;
  private pathParamsSub?: Subscription;
  private destroyed = false;

  constructor(public literalService: LiteralService,
              private activatedRoute: ActivatedRoute,
              public utilsService: UtilsService,
              private usersService: UsersService,
              private vehiclesService: VehiclesService,
              private routeService: RouteService,
              private notificationService: NotificationService,
              private availabilitiesResolver: AvailabilitiesResolver,
              private storageService: StorageService,
              private serviceInfoService: ServiceInfoService,
              public router: Router,
              public appComponent: AppComponent) {}

  async ngOnInit(): Promise<void> {
    if (!this.embedded) {
      this.storageService.setBreadcrumbLoading();
      await this.hydrateUrlFromStorageIfNeeded();
    }

    if (this.embedded) {
      // Path-style setup URLs carry the setup in the path, so param changes must refetch too
      this.pathParamsSub = this.activatedRoute.paramMap.subscribe(() => this.getAvailabilities());
    }
    this.queryParamsSub = this.activatedRoute.queryParamMap.subscribe(async (params) => {
      this.applyQueryParamsToState(params);
      if (this.embedded) this.getAvailabilities();
      const routeIdRaw = params.get('routeId') ?? this.activatedRoute.snapshot.params['routeId'];
      const newRouteId = routeIdRaw ? Number(routeIdRaw) : undefined;
      // Only re-fetch driver/vehicle lists + breadcrumb when the route changes.
      if (newRouteId && newRouteId !== this.routeId) {
        this.routeId = newRouteId;
        const routeDetails: any = await this.routeService.getService(Number(this.routeId));
        this.route = routeDetails;
        this.serviceInfoId = routeDetails?.serviceId;
        this.routeName = routeDetails?.name || '';
        this.managerClientId = routeDetails?.managerClient?.id;


        if (this.serviceInfoId && !this.embedded) {
          const serviceInfo: any = await this.serviceInfoService.getServiceInfo(this.serviceInfoId);
          this.serviceName = serviceInfo?.name || '';
          this.updateBreadcrumb();
        }
      }
    });
  }


  assignedDriverOption() {
    const driver = this.singleAvailbilitySelected?.driver;
    return driver?.id ? [{ value: driver.id, label: driver.name ?? '' }] : [];
  }

  assignedVehicleOption() {
    const vehicle = this.singleAvailbilitySelected?.vehicle;
    return vehicle?.id ? [{ value: vehicle.id, label: vehicle.name ?? '' }] : [];
  }

  private applyQueryParamsToState(params: ParamMap): void {
    const filters = deserializeAvailabilitiesFilters(params);
    this.availabilities.status = filters.selectedStatus;
    this.availabilities.bookings = filters.bookings;
    this.availabilities.fromDate = filters.fromDate as any;
    this.availabilities.toDate = filters.toDate as any;
    this.availabilities.startTime = filters.startTime as any;
    this.availabilities.endTime = filters.endTime as any;

    const pageParam = params.get('availabilitiesPage');
    if (pageParam) {
      const n = Number(pageParam);
      if (!isNaN(n) && n > 0) this.availabilities.pagination.actualPage = n;
    } else {
      this.availabilities.pagination.actualPage = 1;
    }

    const sizeParam = params.get('availabilitiesSize');
    if (sizeParam) {
      const n = Number(sizeParam);
      if (!isNaN(n) && n > 0) this.availabilities.pagination.size = n;
    }

    this.searchValue = params.get('availabilitiesSearch') ?? '';
    this.availabilities.searchValue = this.searchValue;
  }

  private async hydrateUrlFromStorageIfNeeded(): Promise<void> {
    // Availabilities always has ?routeId= in URL, so we never have a truly bare URL.
    // Still preserve "remember my page size" UX: if size isn't in URL, fill from storage.
    const params = this.activatedRoute.snapshot.queryParamMap;
    if (params.has('availabilitiesSize')) return;
    const storedSize = await this.storageService.getAvailabilitiesPageSize();
    // If the user navigated away while we were awaiting storage, don't yank them back.
    if (this.destroyed) return;
    if (!storedSize || storedSize <= 0 || storedSize === APP.DEFAULT_PAGE_SIZE) return;
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { availabilitiesSize: storedSize },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (!this.embedded) this.storageService.clearBreadcrumb();
    this.queryParamsSub?.unsubscribe();
    this.pathParamsSub?.unsubscribe();
  }

  // Namespaced keys so the inherited services-list page/size/search survive the round trip
  private readonly ownParamKeys = [...AVAILABILITY_FILTER_KEYS, 'routeId', 'availabilitiesPage', 'availabilitiesSize', 'availabilitiesSearch'];

  buildBackUrls(): { services: string; service: string; route: string; setup: string } {
    const inherited = { ...this.activatedRoute.snapshot.queryParams };
    for (const key of this.ownParamKeys) delete inherited[key];
    const query = buildQueryString(inherited);
    return {
      services: `/services${query}`,
      service: this.serviceInfoId ? `/services/${this.serviceInfoId}/routes${query}` : `/services${query}`,
      route: this.serviceInfoId ? `/services/${this.serviceInfoId}/routes/${this.routeId}${query}` : `/services${query}`,
      setup: this.serviceInfoId ? `/services/${this.serviceInfoId}/routes/${this.routeId}/setups${query}` : `/services${query}`,
    };
  }

  private updateBreadcrumb(): void {
    const { services, service, route, setup } = this.buildBackUrls();
    this.storageService.setBreadcrumb([
      { labelKey: 'sidebar.services', url: services },
      { label: this.serviceName, url: service },
      { label: this.routeName, url: route },
      { labelKey: 'setup.configuration', url: setup },
      { labelKey: 'setup.availabilities' }
    ]);
  }


  filter(event: { selectedStatus: ExpeditionStatus[], bookings?: boolean, fromDate?: Date, toDate?: Date, startTime?: string, endTime?: string }) {
    const filters: IAvailabilitiesFilters = {
      selectedStatus: event.selectedStatus ?? [],
      bookings: event.bookings,
      fromDate: event.fromDate ? String(event.fromDate) : undefined,
      toDate: event.toDate ? String(event.toDate) : undefined,
      startTime: event.startTime ? String(event.startTime) : undefined,
      endTime: event.endTime ? String(event.endTime) : undefined,
    };
    const queryParams = { ...serializeAvailabilitiesFilters(filters), availabilitiesPage: null };
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams,
      queryParamsHandling: 'merge',
    });
  }

  search(event: any) {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { availabilitiesSearch: event || null, availabilitiesPage: null },
      queryParamsHandling: 'merge',
    });
  }

  async openCreateDeparturesModal() {
    if (this.embedded) {
      // The setup is already selected in the page: no selector needed in the modal
      this.departuresSetups = [];
    } else {
      try {
        const setups: any[] = await this.routeService.getRouteSetups(this.routeId) as any[];
        this.departuresSetups = (setups ?? []).map((setup: any) => ({ value: String(setup.id), label: setup.name }));
      } catch {
        this.departuresSetups = [];
      }
    }
    if (!this.departuresModal) {
      this.departuresModal = new Modal(document.getElementById('createDeparturesModal'), {
        placement: 'center',
        closable: false,
        onHide: () => (this.departuresShowed = false),
        onShow: () => (this.departuresShowed = true),
      });
    }
    this.departuresModal.show();
  }

  async onCreateDeparturesConfirmed(payload: any) {
    const setupId = payload.setupId ?? this.setupId;
    if (!setupId) return;
    this.creatingDepartures = true;
    try {
      await this.routeService.createSetupDepartures(this.routeId, setupId, payload.departures ?? []);
      this.utilsService.showNotification(images.notifications.check, 'availabilities.actions.createDepartures', NotificationTypes.SUCCESS, 'success.title', 'success.text');
      this.departuresModalComponent?.reset();
      this.departuresModal?.hide();
      this.getAvailabilities();
    } catch {
      this.utilsService.showNotification(images.notifications.warning, 'availabilities.actions.createDepartures', NotificationTypes.DANGER, 'error.title', 'error.text');
    } finally {
      this.creatingDepartures = false;
    }
  }

  async getAvailabilities() {
    this.searching = true;
    this.activatedRoute.snapshot.data['availabilities'] = this.availabilities;
    await this.availabilitiesResolver.resolveAsync(this.activatedRoute.snapshot);
    this.searching = false;
  }

  changePage(event: { isNext: boolean, specificPage?: number }) {
    const current = this.availabilities.pagination.actualPage || 1;
    const newPage = event.specificPage ?? (event.isNext ? current + 1 : current - 1);
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { availabilitiesPage: newPage > 1 ? newPage : null },
      queryParamsHandling: 'merge',
    });
  }

  handleChangePaginationSize(newSize: any) {
    const n = Number(newSize);
    const size = (!n || n <= 0) ? APP.DEFAULT_PAGE_SIZE : n;
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { availabilitiesSize: size === APP.DEFAULT_PAGE_SIZE ? null : size, availabilitiesPage: null },
      queryParamsHandling: 'merge',
    });
  }

  getBaseUrlWithRouteId(): string {
    return `${this.router.url.split('?')[0]}?routeId=${this.routeId}`;
  }

  initAvailabilitiesInterval() {
    if (this.interval) clearInterval(this.interval);
    this.interval = setInterval(() => {
      this.getAvailabilities();
    }, 60000);
  }

  clearAvailabilitiesInterval() {
    clearInterval(this.interval);
    this.interval = null;
  }

  resetMultipleSelection() {
    this.availabilities.value = this.availabilities.value.map((availability) => ({...availability, selected: false}));
    this.allAvailbilitiesSelected = false;
  }

  updateSelectableAvailabilities(action?: any) {
    if (this.bulkAction !== action) {
      this.bulkAction = action;
      switch (this.bulkAction) {
        case IBulkActionType.EDIT:
          this.clearAvailabilitiesInterval();
          this.availabilities.value = this.availabilities.value.map((availability) => ({
            ...availability
          }));
          break;
        case IBulkActionType.DELETE:
          this.clearAvailabilitiesInterval();
          this.availabilities.value = this.availabilities.value.map((availability) => ({
            ...availability,
          }));
          break;
        default:
          this.resetMultipleSelection();
          this.initAvailabilitiesInterval();
          break;
      }
    }
  }
  
  async onBulkAction() {
    const selectedAvailabilities = this.availabilitiesSelected;
    if (selectedAvailabilities.length > 0) {
      switch (this.bulkAction) {
        case IBulkActionType.EDIT:
          this.changeDriverVehicleModal = new Modal(document.getElementById('changeDriverVehicle'), {
            placement: 'center',
            closable: false,
            onHide: () => (this.changeDriverVehicleShowed = false),
            onShow: () => {
              const selectedAvailabilities = this.availabilitiesSelected;
              if (selectedAvailabilities.length > 1) {
                this.hasTrips = selectedAvailabilities.some(it => it.tripsIds && it.tripsIds.length > 0);
                this.hasBookings = selectedAvailabilities.some(it => it.bookings === true);
                this.driverSelected = undefined;
                this.vehicleSelected = undefined;
                this.dateTimeSelected = undefined;
                this.timeSelected = undefined;
                this.commentSelected = undefined;
              } else {
                this.hasTrips = this.singleAvailbilitySelected?.tripsIds && this.singleAvailbilitySelected?.tripsIds.length > 0;
                this.hasBookings = this.singleAvailbilitySelected?.bookings === true;
                this.driverSelected = this.singleAvailbilitySelected?.driver?.id;
                this.vehicleSelected = this.vehicles.find((vehicle: any) => vehicle.id === this.singleAvailbilitySelected?.vehicle.id).id;
                this.commentSelected = this.singleAvailbilitySelected?.comment;
                this.dateTimeSelected = this.singleAvailbilitySelected?.startDateTime;
                if (this.dateTimeSelected) {
                  const date = new Date(this.singleAvailbilitySelected!.startDateTime);
                  const hours = String(date.getHours()).padStart(2, '0');
                  const minutes = String(date.getMinutes()).padStart(2, '0');
                  this.timeSelected = `${hours}:${minutes}`;
                }
              }
              this.changeDriverVehicleShowed = true;
            },
          });
          this.changeDriverVehicleModal.show();
          break;
        case IBulkActionType.DELETE:
          this.deleteModal = new Modal(document.getElementById('deleteAvailabilities'), {
            placement: 'center',
            closable: false,
            onHide: () => (this.deleteShowed = false),
            onShow: () => {
              const selectedAvailabilities = this.availabilitiesSelected;
              if (selectedAvailabilities.length > 1) {
                this.deleteModalTitle = this.literalService.get('availabilities.actions.deleteAvailabilities.titleMultiple');
                this.deleteModalText = this.literalService.get('availabilities.actions.deleteAvailabilities.textMultiple').replaceAll('{0}', selectedAvailabilities.length.toString());
                this.hasBookings = selectedAvailabilities.some(it => it.bookings === true);
                this.hasTicketing = selectedAvailabilities.some(it => it.ticketing === true);
                this.isRoundTrip = selectedAvailabilities.some(it => it.roundTrip === true);
              } else {
                this.deleteModalTitle = this.literalService.get('availabilities.actions.deleteAvailabilities.title');
                this.deleteModalText = this.literalService.get('availabilities.actions.deleteAvailabilities.text');
                this.hasBookings = this.singleAvailbilitySelected?.bookings === true;
                this.hasTicketing = this.singleAvailbilitySelected?.ticketing === true;
                this.isRoundTrip = this.singleAvailbilitySelected?.roundTrip === true;
              }
              this.deleteShowed = true;
            },
          });
          this.deleteModal.show();
          break;
      }
    }
  }

  selectAll(event: Event) {
    this.availabilities.value = this.availabilities.value.map((avilability) => ({...avilability, selected: (<HTMLInputElement>event.target).checked}));
    if ((<HTMLInputElement>event.target).checked) this.allAvailbilitiesSelected = true;
  }

  onSelectionChange(availability: IAvailabilities) {
    availability.selected = !availability.selected;
    this.allAvailbilitiesSelected = this.availabilities.value.every((availability) => availability.selected);
  }

  async changeDriverVehicleAction(event: any) {
    this.loadingAction = true;
    await this.routeService.updateAvailabilities(this.routeId, this.availabilitiesSelected.length > 0 ? this.availabilitiesSelected.map(it => it.id!) : [this.singleAvailbilitySelected.id!], event.driverSelected, event.vehicleSelected, event.updateTrips, event.sendNotification, event.timeSelected, event.comment).then(() => {
      this.getAvailabilities();
      this.utilsService.showNotification(images.notifications.check, 'availabilities.actions.edit.success', NotificationTypes.SUCCESS);
      this.changeDriverVehicleModal.toggle();
      this.resetMultipleSelection();
      this.initAvailabilitiesInterval();
    }, () => {
      this.notificationService.image = images.notification.error;
      this.notificationService.title = 'errorOccurred';
      this.notificationService.message = '0';
      this.notificationService.translate = true;
      this.notificationService.show(NotificationTypes.DANGER);
    }).finally(() => {
      this.loadingAction = false;
    })
  }

  async deleteAction(data: any) {
    this.loadingAction = true;
    await this.routeService.deleteAvailabilities(this.routeId, this.availabilitiesSelected.length > 0 ? this.availabilitiesSelected.map(it => it.id!) : [this.singleAvailbilitySelected.id!], data.comment, data?.sendNotification, data?.refund, data?.related, data?.fullRefund).then(() => {
      this.getAvailabilities();
      this.utilsService.showNotification(images.notifications.check, 'availabilities.actions.delete.success', NotificationTypes.SUCCESS);
      this.deleteModal.toggle();
      this.resetMultipleSelection();
      this.initAvailabilitiesInterval();
    }, () => {
      this.notificationService.image = images.notification.error;
      this.notificationService.title = 'errorOccurred';
      this.notificationService.message = '0';
      this.notificationService.translate = true;
      this.notificationService.show(NotificationTypes.DANGER);
    }).finally(() => {
      this.loadingAction = false;
    })
  };

  get availabilitiesSelected() {
    return this.availabilities.value.filter((availability) => availability.selected);
  }

  get deleteBookingsText() {
      return "<label>" + this.literalService.get('availabilities.actions.deleteAvailabilities.extraInfo.exists', true) + "<span class='primary-purple-light-text'>" + this.literalService.get('availabilities.actions.deleteAvailabilities.extraInfo.pendingBookings') + "</span>" + this.literalService.get('availabilities.actions.deleteAvailabilities.extraInfo.thisAvailability' + [this.availabilitiesSelected.length > 1 ? 'Multiple' : '']) + ".</label>";
  }

  tripUrl(availability: IAvailabilities): string {
    return availability.tripsIds.length > 1
      ? `/trips?availability=${availability.id}`
      : `/trips/${availability.tripsIds[0]}`;
  }

  goToTrip(availability: IAvailabilities): void {
    window.open(this.tripUrl(availability), '_blank');
  }

  showEditModal(availability: IAvailabilities) {
    this.driverSelected = availability.driver?.id;
    this.vehicleSelected = this.vehicles.find((vehicle: any) => vehicle.id === availability.vehicle.id)?.id;
    this.singleAvailbilitySelected = availability;
    this.changeDriverVehicleModal = new Modal(document.getElementById('changeDriverVehicle'), {
      placement: 'center',
      closable: false,
      onHide: () => (this.changeDriverVehicleShowed = false),
      onShow: () => {
        this.hasTrips = this.singleAvailbilitySelected?.tripsIds && this.singleAvailbilitySelected?.tripsIds.length > 0;
        this.hasBookings = this.singleAvailbilitySelected?.bookings === true;
        this.driverSelected = this.singleAvailbilitySelected?.driver?.id;
        this.vehicleSelected = this.singleAvailbilitySelected?.vehicle?.id;
        this.commentSelected = this.singleAvailbilitySelected?.comment;
        this.dateTimeSelected = this.singleAvailbilitySelected?.startDateTime;
        if (this.dateTimeSelected) {
          const date = new Date(this.singleAvailbilitySelected!.startDateTime);
          const hours = String(date.getHours()).padStart(2, '0');
          const minutes = String(date.getMinutes()).padStart(2, '0');
          this.timeSelected = `${hours}:${minutes}`;
        }
        this.changeDriverVehicleShowed = true;
      },
    });
    setTimeout(() => this.changeDriverVehicleModal.show(), 0);
  }

  showDeleteModal(availability: IAvailabilities) {
    this.bookings = [];
    this.singleAvailbilitySelected = availability;
    this.deleteModal = new Modal(document.getElementById('deleteAvailabilities'), {
      placement: 'center',
      closable: false,
      onHide: () => (this.deleteShowed = false),
      onShow: () => {
        this.deleteModalTitle = this.literalService.get('availabilities.actions.deleteAvailabilities.title');
        this.deleteModalText = this.literalService.get('availabilities.actions.deleteAvailabilities.text');
        this.hasBookings = this.singleAvailbilitySelected?.bookings === true;
        this.hasTicketing = this.singleAvailbilitySelected?.ticketing === true;
        this.isRoundTrip = this.singleAvailbilitySelected?.roundTrip === true;
        this.deleteShowed = true;
      },
    });
    setTimeout(() => this.deleteModal.show(), 0);
  }

  showCreateBackupModal(availability: IAvailabilities) {
    this.singleAvailbilitySelected = availability;
    this.createBackupModal = new Modal(document.getElementById('createBackup'), {
      placement: 'center',
      closable: false,
      onHide: () => (this.createBackupShowed = false),
      onShow: () => {
        this.driverSelected = undefined;
        this.vehicleSelected = undefined;
        this.commentSelected = undefined;
        this.createBackupShowed = true;
      },
    });
    setTimeout(() => this.createBackupModal.show(), 0);
  }

  async createBackupAction(event: any) {
    this.loadingAction = true;
    await this.routeService.createBackupAvailability(this.routeId, this.singleAvailbilitySelected.id!, event.driverSelected, event.vehicleSelected, event.comment).then(() => {
      this.getAvailabilities();
      this.utilsService.showNotification(images.notifications.check, 'availabilities.actions.createBackup.success', NotificationTypes.SUCCESS);
      this.createBackupModal.toggle();
      this.initAvailabilitiesInterval();
    }, () => {
      this.notificationService.image = images.notification.error;
      this.notificationService.title = 'errorOccurred';
      this.notificationService.message = '0';
      this.notificationService.translate = true;
      this.notificationService.show(NotificationTypes.DANGER);
    }).finally(() => {
      this.loadingAction = false;
    })
  }

}
